// @ts-strict-ignore
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { antGrey } from '@/components/styles/globalStyles';
import Divider from 'antd/lib/divider';
import { SelectProps } from 'antd/lib/select';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import { Integration, IntegrationType } from '../types';

export const integrationSelectOptions = (integrations: Integration[]): SelectProps['options'] =>
    integrations.map((integration) => {
        const iconAndAlt = iconAndAltForIntegrationType(integration.id);
        const integrationIdentifiers = getIntegrationIdentifiers(integration);
        const integrationAlias =
            typeof integration.alias === 'string' && integration.alias.trim().length > 0 ?
                integration.alias.trim()
            :   null;
        return {
            value: integration.installationId,
            title: [integration.name, integrationAlias, ...integrationIdentifiers].filter(Boolean).join(' '),
            label: (
                <Space
                    split={
                        <Divider
                            type="vertical"
                            style={{
                                backgroundColor: antGrey.grey5,
                                height: 18,
                                margin: 0,
                            }}
                        />
                    }
                >
                    <Space align="center" size={8}>
                        <img src={iconAndAlt[0]} alt={iconAndAlt[1]} width={20} height={20} />
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: integrationAlias ? '6px' : undefined,
                            }}
                        >
                            <Text>{integration.name}</Text>
                            {integrationAlias && <Text type="secondary">{integrationAlias}</Text>}
                        </div>
                    </Space>
                    {integrationIdentifiers.map((identifier) => (
                        <Text>{identifier}</Text>
                    ))}
                </Space>
            ),
        };
    });

export const getIntegrationIdentifiers: (integration: Integration) => string[] = (integration) => {
    const fallback = [integration.installationId];
    const normalize = (ids: (string | undefined | null)[] | undefined): string[] => {
        if (ids == null) return fallback;
        const filtered = ids.map((id) => (typeof id === 'string' ? id.trim() : '')).filter((id) => id.length > 0);
        return filtered.length > 0 ? filtered : fallback;
    };

    let candidateIds: (string | undefined | null)[] | undefined;
    switch (integration.id) {
        case IntegrationType.GitHubVCSDistribution:
        case IntegrationType.GitHubVCS:
        case IntegrationType.GitHubCI:
        case IntegrationType.GitHubIT:
            candidateIds = integration.data?.repoName ? [integration.data.repoName] : undefined;
            break;
        case IntegrationType.Bitbucket:
        case IntegrationType.BitbucketCI:
            candidateIds = integration.data?.selectedRepo?.name ? [integration.data.selectedRepo.name] : undefined;
            break;
        case IntegrationType.GitLabVCS:
        case IntegrationType.GitLabCI:
            candidateIds =
                integration.data?.project?.displayableName ? [integration.data.project.displayableName] : undefined;
            break;
        case IntegrationType.TestFlight:
        case IntegrationType.Apple:
            candidateIds = integration.data?.bundleId ? [integration.data.bundleId] : undefined;
            break;
        case IntegrationType.AzureCI:
        case IntegrationType.AzureIT:
        case IntegrationType.AzureVCS:
            candidateIds =
                integration.data?.selectedProject?.name ? [integration.data.selectedProject.name] : undefined;
            break;
        case IntegrationType.Codemagic:
            candidateIds = integration.data?.appName ? [integration.data.appName] : undefined;
            break;
        case IntegrationType.Bitrise:
            candidateIds = integration.data?.appTitle ? [integration.data.appTitle] : undefined;
            break;
        case IntegrationType.Buildkite:
            candidateIds = integration.data?.org?.name ? [integration.data.org.name] : undefined;
            break;
        case IntegrationType.Bugsnag:
            candidateIds =
                integration.data?.selectedProject?.name ? [integration.data.selectedProject.name] : undefined;
            break;
        case IntegrationType.Sentry:
            candidateIds =
                integration.data?.selectedProject?.displayableName ?
                    [integration.data.selectedProject.displayableName]
                :   undefined;
            break;
        case IntegrationType.EmbraceStabilityMonitoring:
            candidateIds =
                integration.data?.selectedProdApp?.name ? [integration.data.selectedProdApp.name] : undefined;
            break;
        case IntegrationType.TestRail:
        case IntegrationType.BrowserStack:
            candidateIds =
                integration.data?.selectedProject?.name ? [integration.data.selectedProject.name] : undefined;
            break;
        case IntegrationType.Qase:
            candidateIds =
                integration.data?.selectedProject?.title ? [integration.data.selectedProject.title] : undefined;
            break;
        case IntegrationType.Testmo:
            candidateIds =
                integration.data?.selectedProject?.name ? [integration.data.selectedProject.name] : undefined;
            break;
        case IntegrationType.Jenkins:
            candidateIds = integration.data?.workflowName ? [integration.data.workflowName] : undefined;
            break;
        case IntegrationType.Jira:
            candidateIds = integration.data?.selectedProjects?.map((project) => project.name);
            break;
        case IntegrationType.Asana:
        case IntegrationType.Pivotal:
            candidateIds = integration.data?.selectedProjects?.map((project) => project.name);
            break;
        case IntegrationType.Shortcut:
            candidateIds = integration.data?.selectedGroups?.map((group) => group.name);
            break;
        case IntegrationType.Slack:
            candidateIds = integration.data?.primaryChannels?.map((channel) => channel.name);
            break;
        case IntegrationType.CircleCI:
            candidateIds = integration.data?.repoName ? [integration.data.repoName] : undefined;
            break;
        case IntegrationType.Amazon:
            candidateIds = integration.data?.selectedAppId ? [integration.data.selectedAppId] : undefined;
            break;
        case IntegrationType.Google:
        case IntegrationType.GoogleBeta:
            candidateIds = integration.data?.packageName ? [integration.data.packageName] : undefined;
            break;
        case IntegrationType.HuaweiAppGallery:
            candidateIds = integration.data?.appId ? [integration.data.appId] : undefined;
            break;
        case IntegrationType.Linear:
            candidateIds = integration.data?.selectedTeams?.map((team) => team.name);
            break;
        case IntegrationType.TravisCI:
            candidateIds = integration.data?.repoSlug ? [integration.data.repoSlug] : undefined;
            break;
        case IntegrationType.Firebase:
        case IntegrationType.FirebaseBeta:
            candidateIds = integration.data?.projectId ? [integration.data.projectId] : undefined;
            break;
        case IntegrationType.Xray:
        case IntegrationType.Amplitude:
        case IntegrationType.DatadogAnalytics:
        case IntegrationType.GoogleAnalytics:
        case IntegrationType.MixpanelAnalytics:
            candidateIds = [];
            break;
        case IntegrationType.Optimizely:
        case IntegrationType.LaunchDarkly:
            candidateIds =
                integration.data?.selectedProject?.name ? [integration.data.selectedProject.name] : undefined;
            break;
        case IntegrationType.Statsig:
            candidateIds =
                integration.data?.selectedProject?.name ? [integration.data.selectedProject.name] : undefined;
            break;
        case IntegrationType.AmplitudeExperiment:
            candidateIds = integration.data?.selectedProject?.id ? [integration.data.selectedProject.id] : undefined;
            break;
        case IntegrationType.Unleash:
            candidateIds =
                integration.data?.selectedProject?.name ? [integration.data.selectedProject.name] : undefined;
            break;
        case IntegrationType.FirebaseFeatureFlagging:
            candidateIds = integration.data?.projectId ? [integration.data.projectId] : undefined;
            break;
        case IntegrationType.GenericCI:
            candidateIds = integration.data?.workflowName ? [integration.data.workflowName] : undefined;
            break;
        case IntegrationType.AndroidVitals:
            candidateIds = integration.data?.appId ? [integration.data.appId] : undefined;
            break;
        case IntegrationType.XcodeCloud:
            candidateIds = integration.data?.productName ? [integration.data.productName] : undefined;
            break;
        case IntegrationType.Monday:
            candidateIds = integration.data?.selectedBoards?.map((board) => board.name);
            break;
        case IntegrationType.RunwayBuildDistroBeta:
            candidateIds = integration.data?.selectedBucket?.name ? [integration.data.selectedBucket.name] : undefined;
            break;
        case IntegrationType.MicrosoftTeams:
            candidateIds = integration.data?.primaryChannels?.map((channel) => channel.displayName);
            break;
        case IntegrationType.DatadogStabilityMonitoring:
            candidateIds = integration.data?.prodEnvironment ? [integration.data.prodEnvironment] : undefined;
            break;
        case IntegrationType.Crowdin:
        case IntegrationType.Lokalise:
            candidateIds =
                integration.data?.selectedProject?.name ? [integration.data.selectedProject.name] : undefined;
            break;
        case IntegrationType.PagerDutyScheduling:
        case IntegrationType.OpsgenieScheduling:
        case IntegrationType.JiraServiceManagementScheduling:
        case IntegrationType.IncidentIoScheduling:
        case IntegrationType.RootlyScheduling:
            candidateIds =
                integration.data?.selectedSchedule?.name ? [integration.data.selectedSchedule.name] : undefined;
            break;
        case IntegrationType.NewRelicStabilityMonitoring:
            candidateIds =
                integration.data?.prodMobileApplication?.name ?
                    [integration.data.prodMobileApplication.name]
                :   undefined;
            break;
        case IntegrationType.NewRelicAnalytics:
            candidateIds = integration.data?.application?.name ? [integration.data.application.name] : undefined;
            break;
        case IntegrationType.Samsung:
            candidateIds = integration.data?.appId ? [integration.data.appId] : undefined;
            break;
        case IntegrationType.DynatraceStabilityMonitoring:
            candidateIds = integration.data?.prodRumApp?.name ? [integration.data.prodRumApp.name] : undefined;
            break;
        case IntegrationType.LuciqStabilityMonitoring:
        case IntegrationType.LuciqAnalytics:
            candidateIds =
                integration.data?.appSlug ?
                    [
                        integration.data.appMode ?
                            `${integration.data.appSlug} (${integration.data.appMode})`
                        :   integration.data.appSlug,
                    ]
                :   undefined;
            break;
        case IntegrationType.CustomAnalytics:
        case IntegrationType.CustomCI:
            candidateIds = integration.data?.integrationId ? [integration.data.integrationId] : undefined;
            break;
        case IntegrationType.GoogleCalendar:
        case IntegrationType.GooglePersonalCalendar:
        case IntegrationType.OutlookCalendar:
            candidateIds = integration.data?.calendarName ? [integration.data.calendarName] : undefined;
            break;
        default:
            candidateIds = [];
            break;
    }
    return normalize(candidateIds);
};

export const getIntegrationLabel = (integration: Integration): string =>
    [integration.name, integration.alias?.trim(), ...getIntegrationIdentifiers(integration)]
        .filter(Boolean)
        .join(' | ');
